import { ref } from 'vue'
import { subscriptionService, type Subscription, type SubscriptionResponse } from '../services/subscription'
import { FREE_ACCESS_SUBSCRIPTION, IS_FREE_ACCESS_MODE } from '../config/freeAccess'
import { isSubscriptionNotFoundError, subscriptionRequiresPlanSelection } from '../utils/subscriptionErrors'

// Global cached state for subscription data
const cachedSubscriptionStatus = ref<SubscriptionResponse | null>(IS_FREE_ACCESS_MODE ? FREE_ACCESS_SUBSCRIPTION : null)
const cachedIsLoadingSubscription = ref(false)
const cachedSubscriptionError = ref<string | null>(null)
const cachedRequiresPlanSelection = ref(false)
const hasSubscriptionFetched = ref(IS_FREE_ACCESS_MODE)
const lastSubscriptionFetch = ref<number>(IS_FREE_ACCESS_MODE ? Date.now() : 0)
const SUBSCRIPTION_CACHE_TTL = 5 * 60 * 1000 // 5 minutes cache

// Debounce mechanism to prevent rapid successive calls
let debounceTimeout: NodeJS.Timeout | null = null
const DEBOUNCE_DELAY = 1000 // 1 second debounce

export const subscriptionRequiresPlanSelectionRef = cachedRequiresPlanSelection

const applySubscriptionResult = (subscription: SubscriptionResponse | null) => {
  cachedSubscriptionStatus.value = subscription
  cachedRequiresPlanSelection.value = IS_FREE_ACCESS_MODE ? false : subscriptionRequiresPlanSelection(subscription)
  hasSubscriptionFetched.value = true
}

const applySubscriptionFetchError = (error: unknown) => {
  if (isSubscriptionNotFoundError(error)) {
    cachedSubscriptionStatus.value = null
    cachedRequiresPlanSelection.value = true
    cachedSubscriptionError.value = null
  } else {
    const errorMessage = error instanceof Error ? error.message : 'Failed to get subscription'
    cachedSubscriptionError.value = errorMessage
  }
  hasSubscriptionFetched.value = true
}

export function useSubscription() {
  const isSubscribing = ref(false)
  const subscriptionError = cachedSubscriptionError
  const currentSubscription = ref<Subscription | null>(null)
  const subscriptionStatus = cachedSubscriptionStatus
  const isLoadingSubscription = cachedIsLoadingSubscription
  const requiresPlanSelection = cachedRequiresPlanSelection

  /**
   * Subscribe to a plan (create + activate)
   * May redirect to Shopify billing confirmation if required
   */
  const subscribeToPlan = async (
    planKey: string,
    couponCode?: string,
  ): Promise<{ subscription: Subscription; redirectUrl?: string } | null> => {
    if (IS_FREE_ACCESS_MODE) {
      return null
    }

    isSubscribing.value = true
    subscriptionError.value = null

    try {
      const result = await subscriptionService.subscribeToplan(planKey, couponCode)
      currentSubscription.value = result.subscription

      // Invalidate cached subscription data since we have a new subscription
      invalidateSubscriptionCache()

      cachedRequiresPlanSelection.value = false

      // Note: Don't redirect immediately here - let the calling code handle the redirectUrl
      // This allows the calling code to show messages, update UI, etc. before redirecting
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to subscribe to plan'
      subscriptionError.value = errorMessage
      return null
    } finally {
      isSubscribing.value = false
    }
  }

  /**
   * Create a subscription without activating
   */
  const createSubscription = async (planKey: string): Promise<Subscription | null> => {
    if (IS_FREE_ACCESS_MODE) {
      return null
    }

    isSubscribing.value = true
    subscriptionError.value = null

    try {
      const subscription = await subscriptionService.createSubscription(planKey)
      currentSubscription.value = subscription
      return subscription
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create subscription'
      subscriptionError.value = errorMessage
      return null
    } finally {
      isSubscribing.value = false
    }
  }

  /**
   * Activate an existing subscription
   * May redirect to Shopify billing confirmation if required
   */
  const activateSubscription = async (subscriptionId: string): Promise<{ redirectUrl?: string } | null> => {
    if (IS_FREE_ACCESS_MODE) {
      return null
    }

    isSubscribing.value = true
    subscriptionError.value = null

    try {
      const response = await subscriptionService.activateSubscription(subscriptionId)

      // Return the response with redirect URL if present, let calling code handle redirect
      if (response.redirect_url) {
        return { redirectUrl: response.redirect_url }
      }

      return {}
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to activate subscription'
      subscriptionError.value = errorMessage
      return null
    } finally {
      isSubscribing.value = false
    }
  }

  /**
   * Clear subscription error
   */
  const clearError = () => {
    subscriptionError.value = null
  }

  /**
   * Get current subscription details (with caching and debouncing)
   */
  const getCurrentSubscription = async (force = false): Promise<SubscriptionResponse | null> => {
    if (IS_FREE_ACCESS_MODE) {
      subscriptionStatus.value = FREE_ACCESS_SUBSCRIPTION
      cachedRequiresPlanSelection.value = false
      hasSubscriptionFetched.value = true
      lastSubscriptionFetch.value = Date.now()
      return FREE_ACCESS_SUBSCRIPTION
    }

    const now = Date.now()

    // If we have cached data that's still fresh and not forcing, return it
    if (!force && hasSubscriptionFetched.value && now - lastSubscriptionFetch.value < SUBSCRIPTION_CACHE_TTL) {
      return subscriptionStatus.value
    }

    // If already loading, wait for it to complete
    if (isLoadingSubscription.value) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!isLoadingSubscription.value) {
            clearInterval(interval)
            resolve(subscriptionStatus.value)
          }
        }, 100)
      })
    }

    // Debounce rapid successive calls
    if (debounceTimeout) {
      clearTimeout(debounceTimeout)
    }

    return new Promise((resolve) => {
      debounceTimeout = setTimeout(async () => {
        isLoadingSubscription.value = true
        subscriptionError.value = null

        try {
          const subscription = await subscriptionService.getCurrentSubscription()
          applySubscriptionResult(subscription)
          lastSubscriptionFetch.value = now
          resolve(subscription)
        } catch (error) {
          applySubscriptionFetchError(error)
          resolve(null)
        } finally {
          isLoadingSubscription.value = false
        }
      }, DEBOUNCE_DELAY)
    })
  }

  /**
   * Check if user has an active subscription (using cached data)
   */
  const hasActiveSubscription = async (): Promise<boolean> => {
    if (IS_FREE_ACCESS_MODE) {
      return true
    }

    try {
      // If we already have cached data, use it immediately
      if (hasSubscriptionFetched.value && subscriptionStatus.value) {
        return subscriptionStatus.value.status === 'active'
      }

      // If we're already loading, wait for it to complete
      if (isLoadingSubscription.value) {
        return new Promise((resolve) => {
          const interval = setInterval(() => {
            if (!isLoadingSubscription.value) {
              clearInterval(interval)
              resolve(subscriptionStatus.value?.status === 'active' || false)
            }
          }, 100)
        })
      }

      // Make API call and wait for it to finish
      isLoadingSubscription.value = true
      subscriptionError.value = null

      try {
        const subscription = await subscriptionService.getCurrentSubscription()

        applySubscriptionResult(subscription)
        lastSubscriptionFetch.value = Date.now()

        return subscription?.status === 'active' || false
      } catch (error) {
        applySubscriptionFetchError(error)
        return false
      } finally {
        isLoadingSubscription.value = false
      }
    } catch (error) {
      isLoadingSubscription.value = false
      return false
    }
  }

  /**
   * Verify subscription and update status (force refresh)
   */
  const verifySubscription = async (): Promise<boolean> => {
    if (IS_FREE_ACCESS_MODE) {
      return true
    }

    const subscription = await getCurrentSubscription(true) // Force refresh
    return subscription?.status === 'active' || false
  }

  /**
   * Reset subscription state
   */
  const resetSubscription = () => {
    currentSubscription.value = null
    subscriptionStatus.value = IS_FREE_ACCESS_MODE ? FREE_ACCESS_SUBSCRIPTION : null
    subscriptionError.value = null
    cachedRequiresPlanSelection.value = false
    isSubscribing.value = false
    isLoadingSubscription.value = false
    hasSubscriptionFetched.value = IS_FREE_ACCESS_MODE
    lastSubscriptionFetch.value = IS_FREE_ACCESS_MODE ? Date.now() : 0
  }

  /**
   * Invalidate cached subscription data (useful after subscription changes)
   */
  const invalidateSubscriptionCache = () => {
    hasSubscriptionFetched.value = false
    lastSubscriptionFetch.value = 0
  }

  return {
    // State
    isSubscribing,
    subscriptionError,
    currentSubscription,
    subscriptionStatus,
    isLoadingSubscription,
    requiresPlanSelection,

    // Actions
    subscribeToPlan,
    createSubscription,
    activateSubscription,
    getCurrentSubscription,
    hasActiveSubscription,
    verifySubscription,
    clearError,
    resetSubscription,
    invalidateSubscriptionCache,
  }
}
